import { Component, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RentalObjectSearchService } from '../../../common/service/data/property-base-search.service';
import { FormComponentBase2 } from '../../../core/component/forms/form-component.base2';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { standardCommand } from '../../../core/model/command';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { RentalObjectKey, RentalObjectKeyHandover, RentalObjectKeyHandoverDirection } from '../../model/rental-object-key';
import { KeyTypeService } from '../../service/data/key-type.service';
import { RentalObjectKeyService } from '../../service/data/rental-object-key.service';

@Component({
    selector: 'app-rental-object-key-form',
    templateUrl: './rental-object-key-form.component.html',
    styleUrls: ['./rental-object-key-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: RentalObjectKeyService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentalObjectKeyFormComponent extends FormComponentBase2<RentalObjectKeyService> {
  constructor(
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    private _sidebarService: SidebarService,
    cdf: ColumnDefinitionFactory,
    rentalObjectService: RentalObjectSearchService,
    keyTypeService: KeyTypeService,
    activatedRoute: ActivatedRoute,
    dataService: RentalObjectKeyService,
    dispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, dataService, messageService, navigation, dispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
    this.rentalObjectService = rentalObjectService;
    this.keyTypeService = keyTypeService;
    this.handoversColumnDefinition = [
      cdf.createDateTime('RENTALOBJECT_KEY.REGISTERED_AT', 'registeredAt', ColumnVisibility.mandatory),
      cdf.createTranslation('RENTALOBJECT_KEY.HANDOVER_DIRECTION', 'directionCaption', ColumnVisibility.default),
      cdf.create('COMMON.QUANTITY', 'quantity', ColumnVisibility.default),
      cdf.create('RENTALOBJECT_KEY.ESIGNATURE_STATUS', 'eSignatureStatusText', ColumnVisibility.default),
      cdf.create('ESIGNATURE.SIGNATORIES', 'signatories', ColumnVisibility.default),
      cdf.create('COMMON.NOTE', 'comment', ColumnVisibility.default),
    ];
  }
  @ViewChild('sidebarContent', { static: false }) sidebarContent: TemplateRef<any>;

  keyTypeService: KeyTypeService;
  rentalObjectService: RentalObjectSearchService;
  protected createModel(): RentalObjectKey {
    return new RentalObjectKey();
  }

  handoversColumnDefinition: ColumnDefinition[];

  protected getFormGroup(): FormGroup {
    const group = this._fb.group({
      id: this._fcf.createHidden(this.dataContext.id),
      keyTypeId: this._fcf.createMandatory('KEYTYPE.KEYTYPE', this.dataContext.keyTypeId),
      rentalObjectId: this._fcf.createWithOptions('COMMON.RENTAL_OBJECT', this.dataContext.rentalObjectId, {
        isReadonly: !this.dataContext.isNew,
        required: true,
      }),
      caption: this._fcf.createMaxLength('RENTALOBJECT_KEY.CAPTION', this.dataContext.caption, 100),
      marking: this._fcf.createMaxLength('RENTALOBJECT_KEY.MARKING', this.dataContext.marking, 100),
      supplier: this._fcf.createMaxLength('RENTALOBJECT_KEY.SUPPLIER', this.dataContext.supplier, 100),
      systemNumber: this._fcf.createMaxLength('RENTALOBJECT_KEY.SYSTEM_NUMBER', this.dataContext.systemNumber, 100),
      quantity: this._fcf.createWithOptions('RENTALOBJECT_KEY.QUANTITY', this.dataContext.quantity, {
        required: true,
        validator: Validators.compose([
          Validators.min(0),
          Validators.max(255),
        ]),
      }),
      quantityLentOut: this._fcf.createWithOptions('RENTALOBJECT_KEY.QUANTITY_LENT_OUT', this.dataContext.quantityLentOut, {
        required: true,
        validator: Validators.compose([
          Validators.min(0),
          Validators.max(this.dataContext.quantity), // Initial validator
        ]),
      }),
      quantityInStorage: this._fcf.createWithOptions(
        'RENTALOBJECT_KEY.QUANTITY_IN_STORAGE',
        (this.dataContext.quantity | 0) - (this.dataContext.quantityLentOut | 0),
        { isReadonly: true, validator: Validators.min(0) }
      ),
      key: this._fcf.createMaxLength('RENTALOBJECT_KEY.KEY', this.dataContext.key, 100),
    });

    // Dynamic validator 
    const quantity = group.controls['quantity'];
    const quantityLentOut = group.controls['quantityLentOut'];
    this.AddSubscription(quantity.valueChanges.subscribe(qty => {
      quantityLentOut.setValidators(
        Validators.compose([
          Validators.min(0),
          Validators.max(qty | 0),
        ])
      );
      quantityLentOut.updateValueAndValidity({ emitEvent: false });
    }));

    this.AddSubscription(group.controls['quantity'].valueChanges.subscribe(val => this.updateQuantityInStorage()));
    this.AddSubscription(group.controls['quantityLentOut'].valueChanges.subscribe(val => this.updateQuantityInStorage()));
    return group;
  }

  onHandoverRowClicked(e: { context: RentalObjectKeyHandover; event: any }): void {
    this.openSidebar(e.context);
  }

  updateQuantityInStorage(): void {
    let quantity = this.form.controls['quantity'].value | 0;
    let quantityLentOut = this.form.controls['quantityLentOut'].value | 0;
    let storageControl = this.form.controls['quantityInStorage'];
    storageControl.setValue(quantity - quantityLentOut);
    storageControl.markAsTouched();
  }

  handoverContext: {
    create: boolean;
    rentalObjectKeyId: string;
    keysInStore: number;
    totalKeys: number;
    keysLentOut: number;
    handover: RentalObjectKeyHandover;
  };

  returnKeys() {
    let model = new RentalObjectKeyHandover();
    model.direction = RentalObjectKeyHandoverDirection.ToLandlord;
    model.quantity = this.form.controls['quantityLentOut'].value;
    this.openSidebar(model);
  }

  handoutKeys() {
    let model = new RentalObjectKeyHandover();
    model.direction = RentalObjectKeyHandoverDirection.ToTenant;
    model.quantity = this.form.controls['quantityInStorage'].value;
    this.openSidebar(model);
  }

  openSidebar(handover: RentalObjectKeyHandover) {
    this.handoverContext = {
      create: handover.isNew,
      rentalObjectKeyId: this.dataContext.id,
      keysInStore: this.form.controls['quantityInStorage'].value,
      totalKeys: this.form.controls['quantity'].value,
      handover: handover,
      keysLentOut: this.form.controls['quantityLentOut'].value,
    };
    this._sidebarService.setSidebarContent({ template: this.sidebarContent });
    this._sidebarService.toggleSidebar(true);
  }

  closeSidebar() {
    this._sidebarService.toggleSidebar(false);
    this.handoverContext = null;
  }

  sidebarSubmitted(): void {
    if (this.handoverContext.create) {
      this.dataContext.handovers.splice(0, 0, this.handoverContext.handover);
      this.form.controls['quantityLentOut'].setValue(this.handoverContext.keysLentOut);
      this.form.controls['quantityInStorage'].setValue(this.handoverContext.keysInStore);
    }
    this.closeSidebar();
  }

  sidebarDeleted(): void {
    if (this.handoverContext.create) {
      let index = this.dataContext.handovers.findIndex(h => h == this.handoverContext.handover);
      this.dataContext.handovers.splice(index, 1);
    }
    this.closeSidebar();
    this.navigation.reloadCurrentRoute();
  }
}
